import { useCallback, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Panel, TreeView, toast } from '@/shared/ui';
import { useAgendaParams } from '@/shared/navigation/react/useAgendaParams';
import { useAgendaState } from '@/shared/navigation/react/useAgendaState';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { getPlanningTree, plannerQueryKeys } from './plannerApi';
import { plannerAgenda } from './plannerAgenda';
import { projectFormAgenda } from './project';
import { serieFormAgenda, serieDetailAgenda } from './serie';
import { productFormAgenda, productDetailAgenda } from './product';
import { buildPlannerTreeData, expandToNode } from './plannerTreeModel';

interface PlannerShellPageProps {
  layerId: string;
}

/**
 * Hlavní obsah root planner agendy.
 *
 * Zobrazuje čtyřúrovňový strom (`Project → PlannerProduct → ProductSerie → TermGroup`)
 * nad read modelem `GetPlanningTree`. Agenda zůstává v `list-detail` režimu, ale detail
 * panel se zatím nevykresluje — návrhy detailu vzniknou znovu.
 *
 * Založení projektu (tlačítko „Nový projekt") i jeho editace (akce tužky na řádku projektu)
 * otevírají modální `projectFormAgenda`. Plus akce u produktu/série/termínu zatím jen zobrazí
 * notifikaci, že daný tok není implementovaný.
 */
export function PlannerShellPage({ layerId }: PlannerShellPageProps) {
  const { t } = useTranslation('planner');
  const navigator = useNavigator();
  const { state } = useAgendaParams(plannerAgenda, layerId);
  const agendaState = useAgendaState(plannerAgenda, layerId);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: plannerQueryKeys.tree(),
    queryFn: getPlanningTree,
  });

  const notImplemented = useCallback(() => {
    toast({
      severity: 'info',
      title: t('messages.comingSoonTitle'),
      description: t('messages.notImplemented'),
    });
  }, [t]);

  const openCreateProject = useCallback(() => {
    void navigator.openModal(projectFormAgenda, {});
  }, [navigator]);

  const openEditProject = useCallback(
    (projectId: string) => {
      void navigator.openModal(projectFormAgenda, { projectId });
    },
    [navigator]
  );

  const openCreateProduct = useCallback(
    (projectId: string) => {
      void navigator.openModal(productFormAgenda, { projectId });
    },
    [navigator]
  );

  const openCreateSerie = useCallback(
    (productId: string) => {
      void navigator.openModal(serieFormAgenda, { productId });
    },
    [navigator]
  );

  useEffect(() => {
    if (!state.activeNodeId && data?.projects[0]) {
      agendaState.patch({ activeNodeId: data.projects[0].projectId });
    }
  }, [agendaState, data, state.activeNodeId]);

  const treeData = useMemo(
    () =>
      buildPlannerTreeData(
        data ?? { projects: [] },
        {
          project: t('tree.types.project'),
          product: t('tree.types.product'),
          serie: t('tree.types.serie'),
          term: t('tree.types.term'),
          editProject: t('actions.editProject'),
          addProduct: t('actions.addProduct'),
          addSerie: t('actions.addSerie'),
          addTerms: t('actions.addTerms'),
        },
        {
          onEditProject: openEditProject,
          onAddProduct: openCreateProduct,
          onAddSerie: openCreateSerie,
          onAddTerms: notImplemented,
        }
      ),
    [data, t, notImplemented, openEditProject, openCreateProduct, openCreateSerie]
  );

  const defaultExpandedIds = useMemo(() => {
    const projectIds = data?.projects.map((project) => project.projectId) ?? [];
    if (state.activeNodeId) {
      return [...expandToNode(treeData, state.activeNodeId, projectIds)];
    }
    return projectIds;
  }, [data, state.activeNodeId, treeData]);

  const handleNodeActivate = useCallback(
    (nodeId: string) => {
      agendaState.patch({ activeNodeId: nodeId });
      const node = treeData.nodesById[nodeId];
      // Výběr uzlu produktu otevře jeho detail v pravém panelu (list-detail).
      if (node?.type === 'product') {
        void navigator.openModal(productDetailAgenda, { productId: nodeId });
      } else if (node?.type === 'serie' && node.parentId) {
        // Uzel seriálu nese parentId = productId (viz plannerTreeModel).
        void navigator.openModal(serieDetailAgenda, { productId: node.parentId, serieId: nodeId });
      }
    },
    [agendaState, navigator, treeData]
  );

  return (
    <div className="bg-background h-full min-h-0 p-4" data-testid="planner-shell">
      <Panel
        title={t('tree.title')}
        actions={
          <Button size="sm" onClick={openCreateProject} data-testid="planner-action-add-project">
            {t('actions.addProject')}
          </Button>
        }
        className="min-h-0"
      >
        {isLoading ? (
          <p className="text-muted-foreground text-sm">{t('states.loading')}</p>
        ) : isError ? (
          <Alert severity="error" title={t('states.errorTitle')}>
            {error instanceof Error ? error.message : t('states.errorDescription')}
          </Alert>
        ) : (
          <TreeView
            data={treeData}
            activeNodeId={state.activeNodeId ?? null}
            onNodeActivate={handleNodeActivate}
            defaultExpandedIds={defaultExpandedIds}
          />
        )}
      </Panel>
    </div>
  );
}
